// Single source of truth for the sequential brand-blue maturity color scale.
// The 5-step ramp (level 1 = palest, level 5 = full Wipro accent) is shared by the
// admin distribution bars (via admin/format.ts) and the respondent dimension
// heatmap, so a maturity level renders the same colour everywhere by construction.

// The fixed Wipro accent (mirrors --accent in index.css). The per-engagement accent
// was dropped in the rebrand, so the ramp is computed once against this constant.
const ACCENT = '#2e5bea';

// Ink / white text tokens for cell labels. Ink matches --color-ink; white is used
// on the two deepest steps where ink no longer clears contrast (see heatCell).
const INK = '#0b1533';
const WHITE = '#ffffff';

// The five maturity tiers (SPEC), index 0..4 = level 1..5.
export const MATURITY_TIER_LABELS: [string, string, string, string, string] = [
  'Initial',
  'Repeatable',
  'Defined',
  'Managed',
  'Optimized',
];

// A 5-step sequential ramp derived from an accent (level 1 = palest, level 5 = full
// accent). Computed in JS from the hex so it needs no runtime color-mix support and
// stays consistent across every distribution bar / heatmap cell.
export function maturityRamp(accent: string): [string, string, string, string, string] {
  const ratios: [number, number, number, number, number] = [0.15, 0.38, 0.58, 0.78, 1];
  return ratios.map((r) => mixWithWhite(accent, r)) as [string, string, string, string, string];
}

function mixWithWhite(hex: string, ratio: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m || !m[1]) return hex;
  const int = parseInt(m[1], 16);
  const channels: [number, number, number] = [(int >> 16) & 255, (int >> 8) & 255, int & 255];
  const to2 = (c: number) =>
    Math.round(c * ratio + 255 * (1 - ratio))
      .toString(16)
      .padStart(2, '0');
  return `#${channels.map(to2).join('')}`;
}

// The precomputed ramp on the fixed accent — the canonical maturity color scale.
export const MATURITY_RAMP = maturityRamp(ACCENT);

// Round a 0..5 mean to a maturity tier (1..5); null passes through. Same rounding as
// the tier label, so a cell's colour and its printed level always agree.
export function maturityTier(value: number | null): 1 | 2 | 3 | 4 | 5 | null {
  if (value === null || Number.isNaN(value)) return null;
  return Math.max(1, Math.min(5, Math.round(value))) as 1 | 2 | 3 | 4 | 5;
}

export function maturityTierLabel(value: number | null): string {
  const t = maturityTier(value);
  return t === null ? '—' : MATURITY_TIER_LABELS[t - 1]!;
}

// Heatmap cell styling for a 0..5 score: ramp-step background + a text colour that
// clears contrast on it. Ink reads on the pale steps (levels 1..3); the two deepest
// steps take white (mirrors the level >= 4 switch in DistributionBar). Null = a
// neutral grey cell. Meaning is never colour-alone — callers also print the number.
export function heatCell(value: number | null): { background: string; color: string } {
  const t = maturityTier(value);
  if (t === null) return { background: '#f1f3f8', color: '#5a6478' };
  return { background: MATURITY_RAMP[t - 1]!, color: t >= 4 ? WHITE : INK };
}

// WCAG relative luminance of a #rrggbb colour, and the higher-contrast text colour on it.
function relLuminance(hex: string): number {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m || !m[1]) return 0;
  const int = parseInt(m[1], 16);
  const lin = [(int >> 16) & 255, (int >> 8) & 255, int & 255].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * lin[0]! + 0.7152 * lin[1]! + 0.0722 * lin[2]!;
}

function contrastRatio(a: string, b: string): number {
  const la = relLuminance(a);
  const lb = relLuminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

// Pick ink or white — whichever reads better on this exact fill. Used by the continuous
// heatmap where the fill is any point along the ramp, not one of five fixed steps.
export function pickTextOn(bg: string): string {
  return contrastRatio(bg, WHITE) >= contrastRatio(bg, INK) ? WHITE : INK;
}

// Continuous version of the ramp: a 0..5 mean maps to any point white→accent (not bucketed),
// landing exactly on the MATURITY_RAMP step at integer means so it stays consistent with the
// bucketed tiles. For dense numeric heatmaps (the cross-LOB matrix) where cells show the mean.
export function heatColor(value: number | null): string {
  if (value === null || Number.isNaN(value)) return '#f1f3f8';
  const clamped = Math.max(1, Math.min(5, value));
  const ratio = 0.15 + ((clamped - 1) / 4) * (1 - 0.15);
  return mixWithWhite(ACCENT, ratio);
}

export function heatCellContinuous(value: number | null): { background: string; color: string } {
  if (value === null || Number.isNaN(value)) return { background: '#f1f3f8', color: '#5a6478' };
  const background = heatColor(value);
  return { background, color: pickTextOn(background) };
}
